import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  TextInput,
  Textarea,
  Paper,
  Avatar,
  Badge,
  Accordion,
  ScrollArea,
  Checkbox,
  Box,
  Alert,
  Select,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Eye, Send, Users, AlertTriangle, Mail } from 'react-feather';
import { useInsertAtCursor } from '../../../hooks';
import { MergeTagChips } from './sessions/feedback';
import {
  cohortCoachesApi,
  type CoachAnnouncementRecipient,
} from '../../../services/api/cohortCoaches';

interface SendAnnouncementsModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  onSent: () => void;
}

// Merge tags available for email template
const MERGE_TAGS = [
  { tag: '{{coach_name}}', description: 'Coach first name' },
  { tag: '{{cohort_name}}', description: 'Cohort name' },
  { tag: '{{cohort_code}}', description: 'Cohort code' },
  { tag: '{{partner_name}}', description: 'Partner company name' },
  { tag: '{{program_name}}', description: 'Program name' },
  { tag: '{{group_names}}', description: 'Assigned group names' },
  { tag: '{{group_count}}', description: 'Number of assigned groups' },
  { tag: '{{group_details}}', description: 'Formatted group assignment details' },
  { tag: '{{coach_immersion_date}}', description: 'Coach immersion date' },
  { tag: '{{coach_confirmation_deadline}}', description: 'Confirmation deadline' },
  { tag: '{{coach_group_compensation}}', description: 'Compensation per group' },
  { tag: '{{dashboard_link}}', description: 'Link to coach dashboard' },
  { tag: '{{cm_name}}', description: 'Cohort manager name' },
  { tag: '{{cm_email}}', description: 'Cohort manager email' },
];

// Subject per spec: "You've Been Matched for {{cohort_name}}!"
const DEFAULT_EMAIL_SUBJECT = "You've Been Matched for {{cohort_name}}!";

// Body per spec with group details, partner info, and compensation
const DEFAULT_EMAIL_BODY = `Hi {{coach_name}},

I'm happy to announce we have matched you to a group for the upcoming {{cohort_name}} cohort! The program will kick off with coach immersion on {{coach_immersion_date}}.

Below are the details of your matched group(s).

**Your Assignment**
- Partner: {{partner_name}}
- Program: {{program_name}}
- Cohort: {{cohort_name}} ({{cohort_code}})

{{group_details}}

Please let us know by {{coach_confirmation_deadline}} to secure your spot.

**Some more details:**
- You will receive a flat fee of {{coach_group_compensation}} for each group you coach.
- Coach immersion will be a flat fee (1.5 hours) and each check in (there will be 2 total, each 30 mins) will be a flat fee (payment for all is dependent on attending).
- Orientation and Wrap will be a flat fee each (each one hour, payment dependent on attending)

**Access Your Dashboard**
{{dashboard_link}}

If you have any questions, reach out to {{cm_name}} at {{cm_email}}.

Best,

{{cm_name}}
Program Delivery, Medley`;

export function SendAnnouncementsModal({
  opened,
  onClose,
  cohortId,
  onSent,
}: SendAnnouncementsModalProps) {
  const [allRecipients, setAllRecipients] = useState<CoachAnnouncementRecipient[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [emailSubject, setEmailSubject] = useState(DEFAULT_EMAIL_SUBJECT);
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_BODY);
  const [lastFocusedField, setLastFocusedField] = useState<'subject' | 'body'>('body');
  const subjectField = useInsertAtCursor<HTMLInputElement>();
  const bodyField = useInsertAtCursor<HTMLTextAreaElement>();

  // MED-761: Dynamic preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCoachId, setPreviewCoachId] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [unresolvableTags, setUnresolvableTags] = useState<string[]>([]);
  const [sampleCoach, setSampleCoach] = useState<{ first_name: string; email: string }>({
    first_name: '',
    email: '',
  });

  // MED-761: Test email state
  const [sendingTest, setSendingTest] = useState(false);

  const handleInsertTag = (tag: string) => {
    if (lastFocusedField === 'subject') {
      subjectField.insert(tag, () => emailSubject, setEmailSubject);
    } else {
      bodyField.insert(tag, () => emailBody, setEmailBody);
    }
  };

  // Fetch recipients when modal opens
  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cohortCoachesApi.getAnnouncementRecipients(cohortId);
      setAllRecipients(data);
      // Pre-select only coaches who haven't been announced yet (contract_status = 'pending')
      const pendingCoaches = data.filter((r) => r.contract_status === 'pending');
      setSelectedUserIds(new Set(pendingCoaches.map((r) => r.user_id)));
      // Auto-select first coach for preview
      if (data.length > 0) {
        setPreviewCoachId(data[0].user_id);
      }
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load announcement recipients',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    if (opened) {
      fetchRecipients();
      setShowPreview(false);
      setUnresolvableTags([]);
    }
  }, [opened, fetchRecipients]);

  // Computed values
  const pendingCoaches = useMemo(
    () => allRecipients.filter((r) => r.contract_status === 'pending'),
    [allRecipients]
  );

  const selectedCount = selectedUserIds.size;
  const totalCount = allRecipients.length;

  // Check if all are selected
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const someSelected = selectedCount > 0 && selectedCount < totalCount;

  // Coach options for preview selector
  const coachSelectOptions = useMemo(
    () =>
      allRecipients.map((r) => ({
        value: r.user_id,
        label: [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email,
      })),
    [allRecipients]
  );

  // Toggle individual coach selection
  const toggleCoach = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(allRecipients.map((r) => r.user_id)));
    }
  };

  // MED-761: Load dynamic preview from backend
  const loadPreview = useCallback(async () => {
    if (!previewCoachId) return;
    setPreviewLoading(true);
    try {
      const preview = await cohortCoachesApi.previewAnnouncement(
        cohortId,
        emailSubject,
        emailBody,
        previewCoachId,
      );
      setPreviewSubject(preview.subject);
      setPreviewBody(preview.body);
      setUnresolvableTags(preview.unresolvableTags);
      setSampleCoach(preview.sampleCoach);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate preview',
        color: 'red',
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [cohortId, emailSubject, emailBody, previewCoachId]);

  // MED-761: Toggle preview and load from backend
  const handleTogglePreview = async () => {
    if (!showPreview) {
      setShowPreview(true);
      await loadPreview();
    } else {
      setShowPreview(false);
    }
  };

  // MED-761: Reload preview when coach selection changes
  const handlePreviewCoachChange = async (value: string | null) => {
    setPreviewCoachId(value);
    if (value && showPreview) {
      setPreviewLoading(true);
      try {
        const preview = await cohortCoachesApi.previewAnnouncement(
          cohortId,
          emailSubject,
          emailBody,
          value,
        );
        setPreviewSubject(preview.subject);
        setPreviewBody(preview.body);
        setUnresolvableTags(preview.unresolvableTags);
        setSampleCoach(preview.sampleCoach);
      } catch {
        notifications.show({
          title: 'Error',
          message: 'Failed to generate preview',
          color: 'red',
        });
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  // MED-761: Send test email (AC4-AC7)
  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const result = await cohortCoachesApi.sendTestAnnouncement(
        cohortId,
        emailSubject,
        emailBody,
        previewCoachId || undefined,
      );
      notifications.show({
        title: 'Test Email Sent',
        message: `Test email sent to ${result.email}`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send test email',
        color: 'red',
      });
    } finally {
      setSendingTest(false);
    }
  };

  const handleSend = async () => {
    if (selectedUserIds.size === 0) return;

    setSubmitting(true);
    try {
      const result = await cohortCoachesApi.sendAnnouncements(
        cohortId,
        Array.from(selectedUserIds),
        emailSubject,
        emailBody
      );

      notifications.show({
        title: 'Announcements Sent',
        message: `${result.announcements_sent} coach${result.announcements_sent !== 1 ? 'es' : ''} notified of their assignments`,
        color: 'green',
      });

      onSent();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send announcements',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getCoachName = (r: CoachAnnouncementRecipient) => {
    const name = [r.first_name, r.last_name].filter(Boolean).join(' ');
    return name || r.email;
  };

  const getInitials = (r: CoachAnnouncementRecipient) => {
    if (r.first_name && r.last_name) {
      return `${r.first_name[0]}${r.last_name[0]}`.toUpperCase();
    }
    return r.email[0].toUpperCase();
  };

  // Format status indicator
  const getStatusIndicator = (r: CoachAnnouncementRecipient) => {
    if (r.contract_status === 'pending') {
      return { label: 'Not sent', color: 'gray' };
    }
    if (r.announced_at) {
      const date = new Date(r.announced_at);
      const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return { label: `Previously sent ${formatted}`, color: 'blue' };
    }
    return { label: 'Announced', color: 'blue' };
  };

  // Convert markdown-style bold (**text**) to styled spans for preview
  const renderPreviewBody = (text: string) => {
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return (
          <Text key={index} component="span" fw={600}>
            {part}
          </Text>
        );
      }
      return part.split('\n').map((line, lineIndex) => (
        <span key={`${index}-${lineIndex}`}>
          {lineIndex > 0 && <br />}
          {line}
        </span>
      ));
    });
  };

  const emailReady = emailSubject.trim().length > 0 && emailBody.trim().length > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Send Assignment Announcements"
      size="lg"
    >
      <Stack gap="lg">
        {/* Recipients Section */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={600} size="sm">
                Select coaches to notify
              </Text>
              <Text size="sm" c="dimmed">
                {selectedCount} of {totalCount} coaches selected
              </Text>
            </Group>

            {/* Select All / Deselect All */}
            {totalCount > 0 && (
              <Checkbox
                label={allSelected ? 'Deselect All' : 'Select All'}
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleSelectAll}
                size="sm"
              />
            )}

            {loading ? (
              <Text size="sm" c="dimmed">
                Loading recipients...
              </Text>
            ) : allRecipients.length === 0 ? (
              <Text size="sm" c="dimmed">
                No coaches with group assignments found.
              </Text>
            ) : (
              <ScrollArea.Autosize mah={250}>
                <Stack gap="xs">
                  {allRecipients.map((recipient) => {
                    const status = getStatusIndicator(recipient);
                    const isSelected = selectedUserIds.has(recipient.user_id);

                    return (
                      <Group
                        key={recipient.user_id}
                        gap="sm"
                        wrap="nowrap"
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleCoach(recipient.user_id)}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleCoach(recipient.user_id)}
                          onClick={(e) => e.stopPropagation()}
                          size="sm"
                        />
                        <Avatar size="sm" radius="xl" color="medleyGreen">
                          {getInitials(recipient)}
                        </Avatar>
                        <Stack gap={0} style={{ flex: 1 }}>
                          <Group gap="xs">
                            <Text size="sm" fw={500}>
                              {getCoachName(recipient)}
                            </Text>
                            <Badge size="xs" variant="light" color={status.color}>
                              {status.label}
                            </Badge>
                          </Group>
                          <Group gap={4}>
                            <Users size={10} />
                            <Text size="xs" c="dimmed">
                              {recipient.group_count} group{recipient.group_count !== 1 ? 's' : ''}
                              {recipient.assigned_groups.length > 0 && (
                                <> — {recipient.assigned_groups.map((g) => g.name).join(', ')}</>
                              )}
                            </Text>
                          </Group>
                        </Stack>
                      </Group>
                    );
                  })}
                </Stack>
              </ScrollArea.Autosize>
            )}

            {/* Info about pre-selection */}
            {pendingCoaches.length > 0 && pendingCoaches.length < allRecipients.length && (
              <Text size="xs" c="dimmed" fs="italic">
                Coaches with "Not sent" status are pre-selected. You can also re-send to previously notified coaches.
              </Text>
            )}
          </Stack>
        </Paper>

        {/* Email Customization */}
        <Accordion variant="contained" defaultValue="customize">
          <Accordion.Item value="customize">
            <Accordion.Control>
              <Text size="sm" fw={500}>
                Customize Email
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <TextInput
                  ref={subjectField.ref}
                  label="Subject"
                  placeholder="Email subject line"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.currentTarget.value)}
                  onFocus={() => setLastFocusedField('subject')}
                  onSelect={subjectField.trackCursor}
                />
                <MergeTagChips tags={MERGE_TAGS} onInsert={handleInsertTag} />
                <Textarea
                  ref={bodyField.ref}
                  label="Message"
                  placeholder="Email body"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.currentTarget.value)}
                  onFocus={() => setLastFocusedField('body')}
                  onSelect={bodyField.trackCursor}
                  minRows={10}
                  autosize
                  maxRows={18}
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {/* MED-761: Preview Toggle + Coach Selector */}
        <Group justify="space-between">
          {allRecipients.length > 0 && (
            <Select
              size="xs"
              placeholder="Preview as coach..."
              data={coachSelectOptions}
              value={previewCoachId}
              onChange={handlePreviewCoachChange}
              style={{ width: 220 }}
              clearable={false}
            />
          )}
          <Button
            variant="subtle"
            size="xs"
            leftSection={<Eye size={14} />}
            onClick={handleTogglePreview}
            loading={previewLoading}
            disabled={!previewCoachId}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </Group>

        {/* MED-761: Dynamic Preview Section */}
        {showPreview && !previewLoading && (
          <Paper withBorder p={0} bg="gray.1" style={{ overflow: 'hidden' }}>
            {/* Preview Header */}
            <Box p="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                Preview (as seen by {sampleCoach.first_name || 'Coach'})
              </Text>
            </Box>

            {/* AC3: Unresolvable tags warning */}
            {unresolvableTags.length > 0 && (
              <Box p="xs">
                <Alert
                  icon={<AlertTriangle size={14} />}
                  color="yellow"
                  variant="light"
                  p="xs"
                >
                  <Text size="xs">
                    {unresolvableTags.length} merge tag
                    {unresolvableTags.length !== 1 ? 's' : ''} could not be resolved:{' '}
                    <Text component="span" fw={600} ff="monospace" size="xs">
                      {unresolvableTags.join(', ')}
                    </Text>
                  </Text>
                </Alert>
              </Box>
            )}

            {/* Email Metadata */}
            <Box p="md" bg="white" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Text size="sm" c="dimmed">
                From: <Text span fw={600} c="dark">Medley SGOS</Text> &lt;notifications@sgos.withmedley.com&gt;
              </Text>
              <Text size="sm" fw={600} mt={4}>
                {previewSubject}
              </Text>
            </Box>

            {/* Email Body Preview */}
            <Box p="md" bg="white">
              <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {renderPreviewBody(previewBody)}
              </Text>
            </Box>

            {/* Email Footer */}
            <Box p="sm" bg="gray.1" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
              <Text size="xs" c="dimmed" ta="center">
                This email was sent to {sampleCoach.email || 'coach@example.com'}
              </Text>
            </Box>
          </Paper>
        )}

        {/* Actions */}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          {/* MED-761: Send Test Email (AC4-AC7) */}
          <Button
            variant="outline"
            leftSection={<Mail size={14} />}
            onClick={handleSendTestEmail}
            loading={sendingTest}
            disabled={!emailReady || submitting}
          >
            Send Test Email
          </Button>
          <Button
            leftSection={<Send size={16} />}
            onClick={() => setConfirmOpened(true)}
            loading={submitting}
            disabled={selectedCount === 0 || loading}
          >
            Send to {selectedCount} Coach{selectedCount !== 1 ? 'es' : ''}
          </Button>
        </Group>
      </Stack>

      {/* MED-646: Bulk send confirmation dialog */}
      <Modal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        title="Confirm Email Send"
        size="sm"
        centered
        zIndex={301}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              You are about to send this email to{' '}
              <Text component="span" fw={700}>
                {selectedCount} coach{selectedCount !== 1 ? 'es' : ''}
              </Text>
              . This action cannot be undone.
            </Text>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setConfirmOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              leftSection={<Send size={14} />}
              onClick={() => {
                setConfirmOpened(false);
                handleSend();
              }}
              loading={submitting}
              disabled={submitting}
            >
              Confirm &amp; Send
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}

export default SendAnnouncementsModal;
